/**
 * GoogleOAuthCallback - Handles Google OAuth redirect
 *
 * This page is called after Google OAuth authorization.
 * It exchanges the authorization code for tokens and redirects back to FitTrack.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useFitnessStore } from '../stores/fitnessStore';

const GoogleOAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleOAuthCallback } = useFitnessStore();

  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      // Check for error from Google
      if (error) {
        setStatus('error');
        setErrorMessage(
          error === 'access_denied'
            ? 'Erişim reddedildi. Google hesabınıza bağlanmak için izin vermeniz gerekiyor.'
            : `Google hatası: ${error}`
        );
        return;
      }

      // Validate params
      if (!code || !state) {
        setStatus('error');
        setErrorMessage('Geçersiz OAuth yanıtı. Lütfen tekrar deneyin.');
        return;
      }

      try {
        const success = await handleOAuthCallback(code, state);

        if (success) {
          setStatus('success');
          // Redirect to FitTrack after a short delay
          setTimeout(() => {
            navigate('/fittrack', { replace: true });
          }, 1500);
        } else {
          setStatus('error');
          setErrorMessage('Google hesabı bağlanamadı. Lütfen tekrar deneyin.');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Beklenmeyen bir hata oluştu.');
      }
    };

    processCallback();
  }, [searchParams, handleOAuthCallback, navigate]);

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-orange-400 animate-spin mx-auto mb-6" />
            <h1 className="text-xl font-semibold text-white mb-2">
              Google Hesabı Bağlanıyor...
            </h1>
            <p className="text-gray-400">
              Lütfen bekleyin, işlem tamamlanıyor.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Bağlantı Başarılı!
            </h1>
            <p className="text-gray-400">
              Google hesabınız bağlandı. FitTrack'e yönlendiriliyorsunuz...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Bağlantı Başarısız
            </h1>
            <p className="text-gray-400 mb-6">{errorMessage}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/fittrack', { replace: true })}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                FitTrack'e Dön
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors"
              >
                Tekrar Dene
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
